// src/components/about/ExperienceTimeline.styles.ts
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../../styles/breakpoints';
export { SectionHeader, Eyebrow, EyebrowMark, Heading, Lede } from './SectionHeader.styles';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

const trunkX = '0.35rem';

/* ---------- Section shell ---------- */

export const TimelineRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['3xl']} 0;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const TimelineContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

/* ---------- Position bar (the single dated role) ---------- */

export const PositionBar = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  @media ${minDevice.laptop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

export const PositionMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
`;

export const Company = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.2rem;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.text};
`;

export const PositionRole = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const PositionMeta = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.lg};
  flex: 0 0 auto;
`;

export const PositionPeriod = styled.span`
  font-family: ${monoStack};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text};
`;

export const PositionDuration = styled.span`
  font-family: ${monoStack};
  font-size: 0.75rem;
  padding: 0.22rem 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

/* ---------- Desktop journey ---------- */

export const DesktopJourney = styled.div`
  display: none;

  @media ${minDevice.laptop} {
    display: grid;
    grid-template-columns: minmax(0, 19rem) minmax(0, 1fr);
    gap: 3rem;
    align-items: start;
    margin-top: ${({ theme }) => theme.spacing['2xl']};
  }
`;

export const JourneyRail = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const JourneyTrunk = styled(motion.span)`
  position: absolute;
  top: 1.4rem;
  bottom: 1.4rem;
  left: ${trunkX};
  width: 1px;
  background: ${({ theme }) => theme.borderColor};
  transform-origin: top center;
  pointer-events: none;
`;

export const JourneyNode = styled.button<{ $active: boolean; $last: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.75rem;
  width: 100%;
  padding: 0.9rem 0.9rem 0.9rem 1.75rem;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    transform: none;
    box-shadow: none;
    background: ${({ theme }) => theme.cardBg};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.borderColor};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const NodeDot = styled.span<{ $active: boolean; $seen: boolean }>`
  position: absolute;
  top: 1.05rem;
  left: 0;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $active, theme }) => ($active ? theme.accent : theme.background)};
  border: 1px solid
    ${({ $active, $seen, theme }) => ($active || $seen ? theme.accent : theme.borderColor)};
  transition: background-color 0.2s, border-color 0.2s, transform 0.2s;
  transform: ${({ $active }) => ($active ? 'scale(1.15)' : 'scale(1)')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const NodeText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

export const NodeClient = styled.span<{ $active: boolean }>`
  font-family: ${monoStack};
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
`;

export const NodeDomain = styled.span<{ $active: boolean }>`
  font-family: ${sansStack};
  font-size: 0.92rem;
  line-height: 1.45;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
`;

export const NodeRef = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.6;
`;

/* ---------- Engagement panel ---------- */

export const EngagementPanel = styled.div`
  display: none;

  @media ${minDevice.laptop} {
    display: flex;
    min-height: 32rem;
    padding: ${({ theme }) => theme.spacing.xl};
    background: ${({ theme }) => theme.cardBg};
    border: ${({ theme }) => theme.cardBorder};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
`;

export const EngagementBody = styled(motion.div)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const EngagementHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const EngagementClient = styled.p`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const EngagementTitle = styled.h3`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.3rem;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.text};
`;

/* ---------- Shared content blocks ---------- */

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

export const BlockLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.7;
`;

export const BlockText = styled.p`
  margin: 0;
  max-width: 70ch;
  font-family: ${sansStack};
  font-size: 0.94rem;
  line-height: 1.72;
  color: ${({ theme }) => theme.text};
`;

export const BlockSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  @media ${minDevice.laptop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
  }
`;

export const MarkerList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const MarkerItem = styled.li`
  position: relative;
  padding-left: 1.1rem;
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};

  &::before {
    content: '';
    position: absolute;
    top: 0.6em;
    left: 0;
    width: 5px;
    height: 1px;
    background: ${({ theme }) => theme.accent};
  }
`;

export const TechList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Tech = styled.li`
  font-family: ${monoStack};
  font-size: 0.72rem;
  padding: 0.28rem 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

/* ---------- Expandable detail ---------- */

export const DetailToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  margin-top: auto;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: none;
    box-shadow: none;
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.accent};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.borderColor};
  }

  svg {
    font-size: 0.6rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const DetailRegion = styled(motion.div)`
  overflow: hidden;
`;

export const DetailList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0.9rem 0 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

/* ---------- Mobile journey ---------- */

export const MobileJourney = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media ${minDevice.laptop} {
    display: none;
  }
`;

export const MobileItem = styled.div<{ $isLast: boolean }>`
  position: relative;
  border-top: ${({ theme }) => theme.cardBorder};

  &:first-child {
    border-top: none;
  }

  ${({ $isLast, theme }) =>
    !$isLast &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 1.5rem;
      bottom: 0;
      left: ${trunkX};
      width: 1px;
      background: ${theme.borderColor};
      pointer-events: none;
    }
  `}
`;

export const MobileToggle = styled.button<{ $active: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1.05rem 0.5rem 1.05rem 1.75rem;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.cardBg};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.borderColor};
  }
`;

export const MobileDot = styled.span<{ $active: boolean }>`
  position: absolute;
  top: 1.15rem;
  left: 0;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $active, theme }) => ($active ? theme.accent : theme.background)};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.accent : theme.borderColor)};
  transition: background-color 0.2s, border-color 0.2s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const MobileRegion = styled(motion.div)`
  overflow: hidden;
`;

export const MobileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 0 0.5rem 1.5rem 1.75rem;
`;

export const MobileChevron = styled(motion.span)<{ $active: boolean }>`
  display: inline-flex;
  font-size: 0.65rem;
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
`;

export const MobileText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

export const MobileClient = styled.span<{ $active: boolean }>`
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
`;

export const MobileDomain = styled.span<{ $active: boolean }>`
  font-family: ${sansStack};
  font-size: 0.9rem;
  line-height: 1.45;
  font-weight: 600;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
`;

/* ---------- Scope note ---------- */

export const TimelineNote = styled.p`
  margin: ${({ theme }) => theme.spacing['2xl']} 0 0;
  max-width: 80ch;
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;